import React, { useState, useRef } from 'react';
import toast from 'react-hot-toast';
import api from '../services/api';

const importApi = {
  buildings:   (fd) => api.post('/import/buildings',   fd, { headers: { 'Content-Type': 'multipart/form-data' } }),
  units:       (fd) => api.post('/import/units',       fd, { headers: { 'Content-Type': 'multipart/form-data' } }),
  contractors: (fd) => api.post('/import/contractors', fd, { headers: { 'Content-Type': 'multipart/form-data' } }),
  template:    (kind) => api.get(`/import/template/${kind}`, { responseType: 'blob' }),
};

const TABS = [
  {
    key: 'buildings',
    label: 'Buildings',
    icon: '🏢',
    description: 'Import your building portfolio — names, addresses, strata plans.',
    fields: ['name *', 'address *', 'city', 'province', 'postal_code', 'strata_plan', 'unit_count', 'notes'],
    tip: 'Import buildings first — units and work orders reference them by name.',
  },
  {
    key: 'units',
    label: 'Units & Owners',
    icon: '🏠',
    description: 'Import unit roster with owner contact info. Owner portal accounts are created automatically.',
    fields: ['unit_number *', 'building_name *', 'floor', 'bedrooms', 'bathrooms', 'square_footage', 'monthly_fee', 'is_rented', 'parking_stall', 'owner_name', 'owner_email', 'owner_phone', 'move_in_date', 'notes'],
    tip: 'Owners get a temporary password emailed to them. building_name must match exactly.',
  },
  {
    key: 'contractors',
    label: 'Contractors',
    icon: '🔧',
    description: 'Import your vendor / contractor database.',
    fields: ['name *', 'company', 'email', 'phone', 'specialty', 'license_number', 'insurance_expiry', 'rating', 'notes'],
    tip: 'Specialty values: plumbing, electrical, hvac, general, landscaping, elevator, other.',
  },
];

function ResultCard({ result, type }) {
  if (!result) return null;

  const isUnits = type === 'units';

  return (
    <div className="mt-4 space-y-3">
      {/* Summary badges */}
      <div className="flex flex-wrap gap-2">
        {isUnits ? (
          <>
            <span className="px-3 py-1.5 bg-green-100 text-green-800 rounded-full text-sm font-medium">
              ✓ {result.created_units} units imported
            </span>
            {result.created_owners > 0 && (
              <span className="px-3 py-1.5 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                👤 {result.created_owners} owner accounts created
              </span>
            )}
          </>
        ) : (
          <span className="px-3 py-1.5 bg-green-100 text-green-800 rounded-full text-sm font-medium">
            ✓ {result.created} imported
          </span>
        )}
        {(result.skipped > 0 || result.skipped?.length > 0) && (
          <span className="px-3 py-1.5 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium">
            ⚠ {result.skipped} skipped (already exist)
          </span>
        )}
        {result.errors?.length > 0 && (
          <span className="px-3 py-1.5 bg-red-100 text-red-800 rounded-full text-sm font-medium">
            ✗ {result.errors.length} errors
          </span>
        )}
      </div>

      {/* New owner accounts — show temp passwords */}
      {result.new_owner_accounts?.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <p className="text-sm font-semibold text-blue-900 mb-2">
            👤 New Owner Accounts Created — Save These Passwords
          </p>
          <div className="space-y-1 max-h-40 overflow-y-auto">
            {result.new_owner_accounts.map((o, i) => (
              <div key={i} className="flex items-center gap-3 text-xs font-mono bg-white rounded px-3 py-1.5 border border-blue-100">
                <span className="text-blue-700 truncate flex-1">{o.email}</span>
                <span className="text-gray-500">{o.name}</span>
                <span className="font-bold text-gray-800 shrink-0">pw: {o.temp_password}</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-blue-600 mt-2">Share these passwords with owners so they can log in to the owner portal.</p>
        </div>
      )}

      {/* Errors */}
      {result.errors?.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <p className="text-sm font-semibold text-red-800 mb-2">Errors</p>
          <ul className="space-y-0.5">
            {result.errors.map((e, i) => <li key={i} className="text-xs text-red-700">• {e}</li>)}
          </ul>
        </div>
      )}

      {/* Skipped */}
      {result.skipped_reasons?.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
          <p className="text-sm font-semibold text-yellow-800 mb-2">Skipped (already exist)</p>
          <ul className="space-y-0.5">
            {result.skipped_reasons.map((s, i) => <li key={i} className="text-xs text-yellow-700">• {s}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}

function ImportTab({ tab }) {
  const [file, setFile]         = useState(null);
  const [result, setResult]     = useState(null);
  const [importing, setImporting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const inputRef = useRef();

  const handleFile = (e) => {
    setFile(e.target.files[0]);
    setResult(null);
  };

  const handleImport = async () => {
    if (!file) return;
    setImporting(true);
    setResult(null);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await importApi[tab.key](fd);
      setResult(res.data);
      const count = tab.key === 'units' ? res.data.created_units : res.data.created;
      toast.success(`Imported ${count} ${tab.key}`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const handleDownloadTemplate = async () => {
    setDownloading(true);
    try {
      const res = await importApi.template(tab.key);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `stratapulse_${tab.key}_template.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download template');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* Description */}
      <div className="bg-blue-50 border border-blue-100 rounded-xl px-4 py-3">
        <p className="text-sm text-blue-800">{tab.description}</p>
        <p className="text-xs text-blue-600 mt-1">💡 {tab.tip}</p>
      </div>

      {/* Required columns */}
      <div>
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">CSV Columns</p>
        <div className="flex flex-wrap gap-1.5">
          {tab.fields.map((f) => (
            <span key={f}
              className={`text-xs px-2 py-1 rounded-md font-mono ${f.endsWith('*') ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-gray-100 text-gray-600'}`}>
              {f}
            </span>
          ))}
        </div>
        <p className="text-xs text-gray-400 mt-1.5"><span className="text-red-600">*</span> required</p>
      </div>

      {/* Download template */}
      <button
        onClick={handleDownloadTemplate}
        disabled={downloading}
        className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 font-medium disabled:opacity-50"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
        </svg>
        {downloading ? 'Downloading…' : `Download sample CSV template`}
      </button>

      {/* File picker */}
      <div>
        <input ref={inputRef} type="file" accept=".csv" onChange={handleFile} className="hidden" />
        <div
          onClick={() => inputRef.current.click()}
          className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
            file ? 'border-green-400 bg-green-50' : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50'
          }`}
        >
          {file ? (
            <>
              <p className="text-2xl mb-1">📄</p>
              <p className="text-sm font-semibold text-green-700">{file.name}</p>
              <p className="text-xs text-green-600 mt-0.5">{(file.size / 1024).toFixed(1)} KB — click to change</p>
            </>
          ) : (
            <>
              <p className="text-2xl mb-1">📂</p>
              <p className="text-sm text-gray-600 font-medium">Click to select your CSV file</p>
              <p className="text-xs text-gray-400 mt-0.5">or drag and drop</p>
            </>
          )}
        </div>
      </div>

      {/* Import button */}
      <button
        onClick={handleImport}
        disabled={!file || importing}
        className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl disabled:opacity-40 flex items-center justify-center gap-2 transition-colors"
      >
        {importing ? (
          <>
            <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
            </svg>
            Importing…
          </>
        ) : (
          <>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l4-4m0 0l4 4m-4-4v12" />
            </svg>
            Import {tab.label}
          </>
        )}
      </button>

      <ResultCard result={result} type={tab.key} />
    </div>
  );
}

export default function DataImport() {
  const [activeTab, setActiveTab] = useState('buildings');
  const activeTabData = TABS.find((t) => t.key === activeTab);

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Data Import</h1>
        <p className="text-gray-500 text-sm mt-1">
          Migrate from PowerStrata or any other system — import your buildings, units, owners, and contractors via CSV.
        </p>
      </div>

      {/* Migration banner */}
      <div className="bg-gradient-to-r from-violet-50 to-blue-50 border border-violet-200 rounded-xl px-5 py-4 mb-6 flex items-start gap-3">
        <span className="text-2xl shrink-0">🚀</span>
        <div>
          <p className="font-semibold text-violet-900 text-sm">Switching from PowerStrata?</p>
          <p className="text-xs text-violet-700 mt-0.5">
            Export your data as CSV from PowerStrata, then import here in minutes.
            Start with <strong>Buildings</strong>, then <strong>Units & Owners</strong>, then <strong>Contractors</strong>.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 mb-6">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-sm font-medium transition-colors ${
              activeTab === tab.key
                ? 'bg-white shadow-sm text-gray-900'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <span>{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        {activeTabData && <ImportTab key={activeTab} tab={activeTabData} />}
      </div>
    </div>
  );
}
